import { useState, forwardRef, useRef, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { hasMatch } from 'Utils/strings';
import ErrorMessage from 'Components/Atoms/ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import FloatLabel from 'Components/Atoms/FloatLabel/FloatLabel';
import Dropdown from 'Components/Atoms/Dropdown/Dropdown';
import DropdownContext from 'Components/Atoms/Dropdown/DropdownContext';
import MultiselectTrigger from '../Multiselect/MultiselectTrigger';
import MultiselectItem from '../Multiselect/MultiselectItem';
import OutlineSearch from 'Components/Atoms/OutlineIcons/OutlineSearch';
import { cn } from 'Utils/cn';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineCloseSmall from 'Components/Atoms/OutlineIcons/OutlineCloseSmall';

const MultiselectGrouped = forwardRef(function MultiselectGrouped(props, ref) {
    const {
        options = [],
        optionsSelected = [],
        actions = [],
        onChangeSelected,
        size: selectSize,
        className,
        label,
        renderOption,
        disabled = false,
        floatLabel,
        enableClear = false,
        onClear,
        icon,
        error,
        highlight,
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const { t } = useTranslation('common');
    const [isHighlighted, setIsHighlighted] = useState(highlight || false);
    const [current, setCurrent] = useState(options[0]?.groupId || null);
    const [showSearch, setShowSearch] = useState(false);
    const searchButtonRef = useRef(null);
    const searchInputRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(null);
    const listRef = useRef([]);

    const hasError = !!error;
    const status = isHighlighted ? 'highlight' : hasError ? 'error' : 'default';

    const handleFilter = (ev) => {
        setQuery(ev.target.value);
        setActiveIndex(null);
    };

    useEffect(() => {
        if (!isOpen) {
            setShowSearch(false);
            setActiveIndex(null);
        } else {
            setCurrent(options[0]?.groupId || null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!showSearch) {
            setQuery('');
        }
    }, [showSearch]);

    const optionItems = options.flatMap((option) =>
        option.items.map((item) => ({ ...item, groupId: option.groupId, groupLabel: option.label })),
    );
    const selectedOptions = optionItems.filter((opt) => optionsSelected.includes(opt.id));

    const handleToggle = (value) => {
        if (optionsSelected.includes(value)) {
            onChangeSelected(
                optionsSelected.filter((v) => v !== value),
                false,
            );
        } else {
            onChangeSelected([...optionsSelected, value], true);
        }
    };

    const hasQuery = query.trim().length > 0;
    const optionsFiltered = hasQuery
        ? options
              .map((group) => ({
                  ...group,
                  items: group.items.filter((item) => hasMatch(item.label, query)),
              }))
              .filter((group) => group.items.length > 0)
        : [];
    const currentOptions = options.find((option) => option.groupId === current)?.items || [];

    const visibleItems = hasQuery ? optionsFiltered.flatMap((group) => group.items) : currentOptions;

    const handleFloatingKeyDown = useCallback(
        (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && activeIndex != null && visibleItems[activeIndex]) {
                e.preventDefault();
                handleToggle(visibleItems[activeIndex].id);
            }
        },
        [activeIndex, visibleItems],
    );

    return (
        <div className="relative min-w-0 group">
            <Dropdown
                open={isOpen}
                onOpenChange={(open) => {
                    setIsHighlighted(false);
                    setIsOpen(open);
                }}
                listRef={listRef}
                activeIndex={activeIndex}
                onNavigate={setActiveIndex}
                virtual
                onFloatingKeyDown={handleFloatingKeyDown}
                trigger={
                    <>
                        <FloatLabel label={floatLabel} hasError={hasError} isOpen={isOpen} highlight={isHighlighted} />
                        <MultiselectTrigger
                            size={selectSize}
                            className={className}
                            label={label}
                            disabled={disabled}
                            enableClear={enableClear}
                            onClear={onClear}
                            icon={icon}
                            status={status}
                            isOpen={isOpen}
                            hasSelected={selectedOptions.length > 0}
                            ref={ref}
                        />
                    </>
                }
            >
                <MultiselectGroupedContent
                    showSearch={showSearch}
                    setShowSearch={setShowSearch}
                    searchButtonRef={searchButtonRef}
                    searchInputRef={searchInputRef}
                    options={options}
                    current={current}
                    setCurrent={setCurrent}
                    query={query}
                    handleFilter={handleFilter}
                    hasQuery={hasQuery}
                    currentOptions={currentOptions}
                    optionsFiltered={optionsFiltered}
                    optionsSelected={optionsSelected}
                    handleToggle={handleToggle}
                    renderOption={renderOption}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    listRef={listRef}
                    actions={actions}
                    setIsOpen={setIsOpen}
                    t={t}
                />
            </Dropdown>
            <ErrorMessage message={error} />
        </div>
    );
});

function MultiselectGroupedContent({
    showSearch,
    setShowSearch,
    searchButtonRef,
    searchInputRef,
    options,
    current,
    setCurrent,
    query,
    handleFilter,
    hasQuery,
    currentOptions,
    optionsFiltered,
    optionsSelected,
    handleToggle,
    renderOption,
    activeIndex,
    setActiveIndex,
    listRef,
    actions,
    setIsOpen,
    t,
}) {
    const { getItemProps } = useContext(DropdownContext);

    let searchFlatIdx = 0;

    return (
        <>
            <div className="flex items-center gap-0.5 py-1 px-1.5 bg-gray-100 overflow-x-auto scrollbar-thin">
                <button
                    type="button"
                    ref={searchButtonRef}
                    className="px-2 flex outline-none"
                    onClick={() => setShowSearch(!showSearch)}
                >
                    <OutlineSearch />
                </button>
                {!showSearch &&
                    options.map((option) => (
                        <button
                            className={cn(
                                'whitespace-nowrap text-xs px-3 py-1.5 font-semibold rounded-sm',
                                current === option.groupId && 'font-semibold' && 'bg-white text-dark-blue shadow',
                            )}
                            type="button"
                            key={option.groupId}
                            onClick={() => {
                                setCurrent(option.groupId);
                                setActiveIndex(null);
                            }}
                        >
                            {option.label}
                        </button>
                    ))}
                {showSearch && (
                    <div className="relative w-full">
                        <input
                            className="h-7 focus:outline-none rounded-md w-full pr-7 [&::-webkit-search-cancel-button]:hidden"
                            type="search"
                            autoFocus
                            value={query}
                            onChange={handleFilter}
                            ref={searchInputRef}
                            onBlur={(ev) => {
                                if (ev.relatedTarget === searchButtonRef.current) return;

                                if (query.trim() === '') {
                                    setShowSearch(false);
                                }
                            }}
                        />
                        <IconButton
                            className="absolute right-0 top-1/2 -translate-y-1/2"
                            icon={<OutlineCloseSmall />}
                            iconSize={24}
                            variant="ghost"
                            size="small"
                            onClick={() => {
                                setShowSearch(false);
                            }}
                        />
                    </div>
                )}
            </div>

            <div className="p-1.5 min-h-12">
                {!hasQuery &&
                    currentOptions.map((opt, idx) => (
                        <div
                            key={`list-${opt.id}`}
                            ref={(node) => {
                                listRef.current[idx] = node;
                            }}
                            tabIndex={activeIndex === idx ? 0 : -1}
                            className="outline-none"
                            {...getItemProps({
                                onClick() {
                                    handleToggle(opt.id);
                                },
                                onKeyDown(e) {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleToggle(opt.id);
                                    }
                                },
                            })}
                        >
                            <MultiselectItem
                                checked={optionsSelected.includes(opt.id)}
                                onChange={() => handleToggle(opt.id)}
                                className={opt.className}
                                active={activeIndex === idx}
                            >
                                {renderOption ? renderOption(opt) : opt.label}
                            </MultiselectItem>
                        </div>
                    ))}

                {hasQuery && optionsFiltered.length === 0 && (
                    <div className="text-center text-gray-500 text-sm py-6">{t('no-results-found')}</div>
                )}

                {hasQuery &&
                    optionsFiltered.map((group) => (
                        <div key={`search-${group.id}`} className="mb-2">
                            <div className=" text-gray-500 pl-2 pt-3 pb-1 text-2xs uppercase tracking-wider font-semibold">
                                {group.label}
                            </div>
                            {group.items.map((item) => {
                                const idx = searchFlatIdx++;
                                return (
                                    <div
                                        key={`search-${item.id}`}
                                        ref={(node) => {
                                            listRef.current[idx] = node;
                                        }}
                                        tabIndex={activeIndex === idx ? 0 : -1}
                                        className="outline-none"
                                        {...getItemProps({
                                            onClick() {
                                                handleToggle(item.id);
                                            },
                                            onKeyDown(e) {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleToggle(item.id);
                                                }
                                            },
                                        })}
                                    >
                                        <MultiselectItem
                                            checked={optionsSelected.includes(item.id)}
                                            onChange={() => handleToggle(item.id)}
                                            className={item.className}
                                            active={activeIndex === idx}
                                        >
                                            {renderOption ? renderOption(item) : item.label}
                                        </MultiselectItem>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
            </div>

            {actions.length > 0 && (
                <div
                    className="border-t bg-white border-gray-200 p-1.5 sticky bottom-0 gap-2"
                    onClick={() => setIsOpen(false)}
                >
                    <div className="flex flex-col gap-1">{actions}</div>
                </div>
            )}
        </>
    );
}

export default MultiselectGrouped;

MultiselectGrouped.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.node,
            groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    label: PropTypes.node,
                    className: PropTypes.string,
                }),
            ),
        }),
    ),
    optionsSelected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onChangeSelected: PropTypes.func.isRequired,
    className: PropTypes.string,
    size: PropTypes.oneOf(['normal', 'small']),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    floatLabel: PropTypes.string,
    disabled: PropTypes.bool,
    renderOption: PropTypes.func,
    enableClear: PropTypes.bool,
    onClear: PropTypes.func,
    icon: PropTypes.node,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    highlight: PropTypes.bool,
    actions: PropTypes.arrayOf(PropTypes.node),
};

MultiselectGroupedContent.propTypes = {
    showSearch: PropTypes.bool,
    setShowSearch: PropTypes.func,
    searchButtonRef: PropTypes.object,
    searchInputRef: PropTypes.object,
    options: PropTypes.array,
    current: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setCurrent: PropTypes.func,
    query: PropTypes.string,
    handleFilter: PropTypes.func,
    hasQuery: PropTypes.bool,
    currentOptions: PropTypes.array,
    optionsFiltered: PropTypes.array,
    optionsSelected: PropTypes.array,
    handleToggle: PropTypes.func,
    renderOption: PropTypes.func,
    activeIndex: PropTypes.number,
    setActiveIndex: PropTypes.func,
    listRef: PropTypes.object,
    actions: PropTypes.array,
    setIsOpen: PropTypes.func,
    t: PropTypes.func,
};
